import type { OnDestroy, OnInit } from '@angular/core';
import { Component, inject, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterOutlet } from '@angular/router';
import { SpriFullscreenComponent } from '@spri/components/fullscreen';
import { SpriLoadingBarComponent } from '@spri/components/loading-bar';
import { SpriNavigationService, SpriVerticalNavigationComponent } from '@spri/components/navigation';
import { SpriMediaWatcherService } from '@spri/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import type { Navigation } from 'app/core/navigation/navigation.types';
import { TenantContextService } from 'app/core/services/tenant-context.util';
import { UserService } from 'app/core/user/user.service';
import type { User } from 'app/core/user/user.types';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'spri-futuristic-layout',
  templateUrl: './futuristic.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [
    SpriLoadingBarComponent,
    SpriVerticalNavigationComponent,
    UserComponent,
    MatButtonModule,
    MatIconModule,
    SpriFullscreenComponent,
    SearchComponent,
    ShortcutsComponent,
    NotificationsComponent,
    RouterOutlet,
  ],
})
export class FuturisticLayoutComponent implements OnInit, OnDestroy {
  protected readonly tenantContextService = inject(TenantContextService);
  isScreenSmall: boolean;
  navigation: Navigation;
  user: User;
  private _unsubscribeAll = new Subject<void>();

  /**
   * Constructor
   */
  private _navigationService = inject(NavigationService);
  private _userService = inject(UserService);
  private _spriMediaWatcherService = inject(SpriMediaWatcherService);
  private _SpriNavigationService = inject(SpriNavigationService);

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to navigation data
    this._navigationService.navigation$.pipe(takeUntil(this._unsubscribeAll)).subscribe((navigation: Navigation) => {
      this.navigation = navigation;
    });

    // Subscribe to the user service
    this._userService.user$.pipe(takeUntil(this._unsubscribeAll)).subscribe((user: User) => {
      this.user = user;
    });

    // Subscribe to media changes
    this._spriMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation = this._SpriNavigationService.getComponent<SpriVerticalNavigationComponent>(name);

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }
}
